// src/pages/HelpCentre.jsx
import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  useMediaQuery,
  useTheme,
  alpha,
  Card,
  CardContent,
  Link,
  Divider
} from '@mui/material';
import {
  Search,
  ExpandMore,
  HelpOutline,
  AccountBalance,
  CreditCard,
  Security,
  Phone,
  Email,
  Chat,
  LocationOn,
  AccessTime,
  Description,
  Payment,
  Lock,
  MobileFriendly,
  Sync,
  People,
  School,
  Business,
  Home,
  DirectionsCar,
  ArrowForward,
  CheckCircle,
  Facebook,
  Twitter,
  LinkedIn,
  Instagram
} from '@mui/icons-material';
import { motion } from "framer-motion";

// Professional DiamondBank Zimbabwe color palette
const colors = {
  primary: '#1a1a1a',
  primaryLight: '#2d2d2d',
  primaryDark: '#0d0d0d',
  accent: '#6b21a5',
  accentLight: '#8b5cc9',
  accentDark: '#4c1d6e',
  secondary: '#e5e7eb',
  white: '#ffffff',
  black: '#000000',
  gray: '#6b7280',
  grayLight: '#9ca3af',
  grayDark: '#374151',
  lightBg: '#f8f9fa',
  textDark: '#1a1a1a',
  textLight: '#ffffff'
};

const HelpCentre = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(false);

  const handleFaqChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const categories = [
    { icon: <AccountBalance sx={{ fontSize: 40 }} />, title: "Account Management", color: colors.accent, count: 8 },
    { icon: <CreditCard sx={{ fontSize: 40 }} />, title: "Cards & Payments", color: colors.accent, count: 6 },
    { icon: <Security sx={{ fontSize: 40 }} />, title: "Security & Fraud", color: colors.accent, count: 5 },
    { icon: <MobileFriendly sx={{ fontSize: 40 }} />, title: "Mobile Banking", color: colors.accent, count: 7 },
    { icon: <Payment sx={{ fontSize: 40 }} />, title: "Transfers & Payments", color: colors.accent, count: 6 },
    { icon: <HelpOutline sx={{ fontSize: 40 }} />, title: "General Enquiries", color: colors.accent, count: 4 }
  ];

  const popularTopics = [
    { title: "How to open an account", icon: <AccountBalance />, link: "/help/open-account" },
    { title: "Reset online banking password", icon: <Lock />, link: "/help/reset-password" },
    { title: "Report lost or stolen card", icon: <CreditCard />, link: "/help/lost-card" },
    { title: "Mobile app troubleshooting", icon: <MobileFriendly />, link: "/help/mobile-app" },
    { title: "International transfers", icon: <Sync />, link: "/help/international-transfers" },
    { title: "Fraud protection", icon: <Security />, link: "/help/fraud-protection" }
  ];

  const faqs = [
    {
      id: 'panel1',
      question: "How do I open a DiamondBank Zimbabwe account?",
      answer: "You can open an account online in minutes! Visit our Personal Banking page and click 'Open an Account'. You'll need your ID, proof of address, and a valid phone number. Alternatively, visit any DiamondBank Zimbabwe branch with the required documents."
    },
    {
      id: 'panel2',
      question: "What are the account maintenance fees?",
      answer: "Silver Account: ZWL 2,500/month, Gold Account: ZWL 8,000/month, Platinum Account: ZWL 15,000/month, Diamond Account: ZWL 35,000/month. Students and seniors may qualify for fee reductions."
    },
    {
      id: 'panel3',
      question: "How do I activate my new debit card?",
      answer: "You can activate your card through our mobile app, online banking, or by calling customer service. Simply follow the instructions included with your card or visit any DiamondBank Zimbabwe ATM to set your PIN."
    },
    {
      id: 'panel4',
      question: "What should I do if I forget my online banking password?",
      answer: "Click 'Forgot Password' on the login page. You'll receive an OTP via SMS to verify your identity, then you can create a new password. If you're still having issues, contact our support team."
    },
    {
      id: 'panel5',
      question: "How long do international transfers take?",
      answer: "International transfers typically take 2-5 business days, depending on the destination and intermediary banks. DiamondBank Zimbabwe customers get priority processing."
    },
    {
      id: 'panel6',
      question: "Is my money safe with DiamondBank Zimbabwe?",
      answer: "Yes! DiamondBank Zimbabwe is licensed by the Reserve Bank of Zimbabwe and all deposits are protected up to ZWL 50,000,000 by the Deposit Protection Scheme. We use 256-bit encryption and multi-factor authentication to protect your account."
    },
    {
      id: 'panel7',
      question: "How do I report a lost or stolen card?",
      answer: "Call our 24/7 emergency hotline immediately at +263 242 708 888. You can also block your card instantly through the mobile app under 'Card Management'."
    },
    {
      id: 'panel8',
      question: "What are the branch operating hours?",
      answer: "Monday to Friday: 8:30 AM - 4:30 PM, Saturday: 9:00 AM - 1:00 PM. Select branches in Harare and Bulawayo offer extended hours. Check our branch locator for specific hours."
    }
  ];

  const filteredFaqs = searchQuery
    ? faqs.filter(faq => 
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqs;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ overflowX: 'hidden', backgroundColor: colors.lightBg }}>
        {/* Hero Section */}
        <Box
          sx={{
            height: 'auto',
            minHeight: '350px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${colors.primary}dd 0%, ${colors.accent}99 100%)`,
            color: 'white',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            py: 8
          }}
        >
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.08,
            backgroundImage: `radial-gradient(circle at 20% 50%, ${colors.white} 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }} />
          
          <Container>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <HelpOutline sx={{ fontSize: 60, color: colors.accent, mb: 2 }} />
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '3rem' },
                  mb: 2
                }}
              >
                How Can We <span style={{ color: colors.accent }}>Help You?</span>
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  maxWidth: '700px',
                  mx: 'auto',
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  fontWeight: 400,
                  opacity: 0.9,
                  mb: 4
                }}
              >
                Find answers to your questions, get support, or contact our customer service team in Zimbabwe
              </Typography>

              {/* Search Bar */}
              <Paper
                elevation={0}
                sx={{
                  maxWidth: '600px',
                  mx: 'auto',
                  borderRadius: '50px',
                  overflow: 'hidden',
                  backgroundColor: colors.white,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { border: 'none' }
                  }
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Search for answers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: colors.accent, ml: 2 }} />
                      </InputAdornment>
                    ),
                    sx: { py: 1.5 }
                  }}
                />
              </Paper>
            </motion.div>
          </Container>
        </Box>

        <Container sx={{ py: 8 }}>
          {/* Quick Help Categories */}
          <Box sx={{ mb: 8 }}>
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography variant="subtitle1" sx={{ color: colors.accent, fontWeight: 600, letterSpacing: 1, mb: 1 }}>
                BROWSE BY TOPIC
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: colors.primary,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80px',
                    height: '3px',
                    background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                    borderRadius: '2px'
                  }
                }}
              >
                Help Categories
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {categories.map((category, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: '16px',
                        background: 'white',
                        boxShadow: `0 4px 16px ${alpha(colors.primary, 0.05)}`,
                        border: `1px solid ${alpha(colors.gray, 0.1)}`,
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        textAlign: 'center',
                        '&:hover': {
                          boxShadow: `0 8px 24px ${alpha(colors.accent, 0.1)}`,
                          borderColor: alpha(colors.accent, 0.2)
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          backgroundColor: alpha(colors.accent, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          mx: 'auto',
                          color: colors.accent
                        }}
                      >
                        {category.icon}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: colors.primary, mb: 1 }}>
                        {category.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.gray }}>
                        {category.count} articles
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Popular Topics */}
          <Box sx={{ mb: 8 }}>
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography variant="subtitle1" sx={{ color: colors.accent, fontWeight: 600, letterSpacing: 1, mb: 1 }}>
                MOST POPULAR
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: colors.primary,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80px',
                    height: '3px',
                    background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                    borderRadius: '2px'
                  }
                }}
              >
                Popular Topics
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {popularTopics.map((topic, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Button
                      fullWidth
                      href={topic.link}
                      sx={{
                        justifyContent: 'flex-start',
                        p: 2,
                        borderRadius: '12px',
                        color: colors.textDark,
                        textTransform: 'none',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: alpha(colors.accent, 0.05),
                          transform: 'translateX(5px)'
                        }
                      }}
                    >
                      <Box sx={{ color: colors.accent, mr: 2 }}>{topic.icon}</Box>
                      <Typography variant="body1" sx={{ flex: 1 }}>{topic.title}</Typography>
                      <ArrowForward sx={{ fontSize: 18, opacity: 0.5 }} />
                    </Button>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* FAQ Section */}
          <Box sx={{ mb: 8 }}>
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography variant="subtitle1" sx={{ color: colors.accent, fontWeight: 600, letterSpacing: 1, mb: 1 }}>
                FREQUENTLY ASKED
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: colors.primary,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80px',
                    height: '3px',
                    background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                    borderRadius: '2px'
                  }
                }}
              >
                Frequently Asked Questions
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                borderRadius: '20px',
                background: 'white',
                boxShadow: `0 8px 24px ${alpha(colors.primary, 0.05)}`,
                border: `1px solid ${alpha(colors.gray, 0.1)}`,
                overflow: 'hidden'
              }}
            >
              {filteredFaqs.length > 0 ? (
                filteredFaqs.map((faq) => (
                  <Accordion
                    key={faq.id}
                    expanded={expandedFaq === faq.id}
                    onChange={handleFaqChange(faq.id)}
                    sx={{
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        backgroundColor: alpha(colors.accent, 0.02)
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: colors.accent }} />}
                      sx={{
                        '&:hover': { backgroundColor: alpha(colors.gray, 0.02) },
                        borderRadius: '12px'
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, color: colors.primary }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body1" sx={{ color: colors.gray, lineHeight: 1.7 }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                  <HelpOutline sx={{ fontSize: 60, color: colors.gray, mb: 2 }} />
                  <Typography variant="h5" sx={{ color: colors.primary, mb: 1 }}>
                    No results found
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.gray }}>
                    Try searching with different keywords or browse our categories
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>

          {/* Contact Support Section - Updated for Zimbabwe */}
          <Box>
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography variant="subtitle1" sx={{ color: colors.accent, fontWeight: 600, letterSpacing: 1, mb: 1 }}>
                STILL NEED HELP?
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: colors.primary,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80px',
                    height: '3px',
                    background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                    borderRadius: '2px'
                  }
                }}
              >
                Contact Our Support Team
              </Typography>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      borderRadius: '20px',
                      background: 'white',
                      boxShadow: `0 4px 16px ${alpha(colors.primary, 0.05)}`,
                      border: `1px solid ${alpha(colors.gray, 0.1)}`,
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `0 8px 24px ${alpha(colors.accent, 0.1)}`
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        backgroundColor: alpha(colors.accent, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        mx: 'auto',
                        color: colors.accent
                      }}
                    >
                      <Phone sx={{ fontSize: 35 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary, mb: 2 }}>
                      Call Us
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.gray, mb: 1 }}>
                      Monday - Friday: 8:30 AM - 4:30 PM (Zimbabwe Time)
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.gray, mb: 2 }}>
                      Saturday: 9:00 AM - 1:00 PM
                    </Typography>
                    <Typography variant="h6" sx={{ color: colors.accent, fontWeight: 700 }}>
                      +263 242 708 888
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>

              <Grid item xs={12} md={4}>
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      borderRadius: '20px',
                      background: 'white',
                      boxShadow: `0 4px 16px ${alpha(colors.primary, 0.05)}`,
                      border: `1px solid ${alpha(colors.gray, 0.1)}`,
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `0 8px 24px ${alpha(colors.accent, 0.1)}`
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        backgroundColor: alpha(colors.accent, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        mx: 'auto',
                        color: colors.accent
                      }}
                    >
                      <Email sx={{ fontSize: 35 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary, mb: 2 }}>
                      Email Us
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.gray, mb: 1 }}>
                      General Inquiries
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.accent, mb: 2 }}>
                      info@diamondbank.co.zw
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.gray, mb: 1 }}>
                      Support
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.accent }}>
                      support@diamondbank.co.zw
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>

              <Grid item xs={12} md={4}>
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      borderRadius: '20px',
                      background: 'white',
                      boxShadow: `0 4px 16px ${alpha(colors.primary, 0.05)}`,
                      border: `1px solid ${alpha(colors.gray, 0.1)}`,
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `0 8px 24px ${alpha(colors.accent, 0.1)}`
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        backgroundColor: alpha(colors.accent, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        mx: 'auto',
                        color: colors.accent
                      }}
                    >
                      <LocationOn sx={{ fontSize: 35 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary, mb: 2 }}>
                      Visit Us
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.gray, mb: 1 }}>
                      Head Office - Harare
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.gray, mb: 2 }}>
                      80 Nelson Mandela Avenue, Harare, Zimbabwe
                    </Typography>
                    <Button
                      variant="outlined"
                      href="/locations"
                      sx={{
                        borderRadius: '50px',
                        borderColor: colors.accent,
                        color: colors.accent,
                        '&:hover': {
                          borderColor: colors.accentLight,
                          backgroundColor: alpha(colors.accent, 0.05)
                        }
                      }}
                    >
                      Find a Branch in Zimbabwe
                    </Button>
                  </Paper>
                </motion.div>
              </Grid>
            </Grid>
          </Box>

          {/* Emergency Support Notice - Updated for Zimbabwe */}
          <Box sx={{ mt: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: '20px',
                background: `linear-gradient(135deg, ${alpha(colors.accent, 0.05)} 0%, ${alpha(colors.primary, 0.05)} 100%)`,
                border: `1px solid ${alpha(colors.accent, 0.2)}`,
                textAlign: 'center'
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary, mb: 2 }}>
                Emergency Support Available 24/7
              </Typography>
              <Typography variant="body1" sx={{ color: colors.gray, mb: 3 }}>
                For lost or stolen cards, fraud alerts, or urgent account issues in Zimbabwe, call our emergency hotline
              </Typography>
              <Typography variant="h4" sx={{ color: colors.accent, fontWeight: 800 }}>
                +263 242 708 888
              </Typography>
              <Typography variant="caption" sx={{ color: colors.gray, mt: 2, display: 'block' }}>
                Available 24 hours a day, 7 days a week for Zimbabwe customers
              </Typography>
            </Paper>
          </Box>
        </Container>
      </Box>
    </motion.div>
  );
};

export default HelpCentre;